import { AvatarPair } from "../../components/Avatar.js";
import { useAuth } from "../../lib/auth.js";
import {
  buildStandings,
  TIEBREAK_LABEL,
  type StandingRow,
  type StandingsGroup,
} from "../../lib/standings.js";
import type { TieTeam } from "../../lib/tie.js";
import type { TournamentEvent } from "../../lib/types.js";
import { rosterOf, sideNames, sidePeople, useDraw, useTieTeams, type Person } from "./shared.js";

/**
 * Everything a group table needs, computed once the same way wherever one is drawn.
 *
 * `tabled` is false for the formats that have no table of this kind: a knockout (the draw
 * is the standing) and americano / mexicano (ranked per player, not per side).
 */
export function useGroupTables(event: TournamentEvent) {
  const { member } = useAuth();
  const draw = useDraw(event.id);
  const { data: teams } = useTieTeams(event);
  const roster = rosterOf(event);
  const fixtures = draw.data ?? [];

  const sideOf = new Map<string, string>();
  for (const team of teams ?? []) {
    for (const player of team.members) sideOf.set(player.memberId, team.side);
  }

  const isTie = event.format === "team_tie";
  const tabled = event.format === "round_robin" || event.format === "group_knockout" || isTie;

  const groups = tabled
    ? buildStandings(fixtures, isTie ? (ids) => sideOf.get(ids[0] ?? "") ?? null : undefined)
    : [];

  const yourSide = member ? sideOf.get(member.id) : undefined;

  return {
    tabled,
    isTie,
    groups,
    fixtures,
    isPending: draw.isPending,
    roster,
    label: (row: StandingRow) =>
      isTie ? teamName(teams, row.key) : sideNames(row.memberIds, roster),
    faces: (row: StandingRow): Person[] | null =>
      isTie ? null : sidePeople(row.memberIds, roster),
    isYours: (row: StandingRow) =>
      member ? (isTie ? row.key === yourSide : row.memberIds.includes(member.id)) : false,
  };
}

const teamName = (teams: TieTeam[] | undefined, side: string) =>
  teams?.find((team) => team.side === side)?.name ?? `Side ${side}`;

/**
 * One group's table, as a scoresheet: a ruled head on a heavy line, a rank in the display
 * face, the pair, and played / won / game difference in mono so the columns line up.
 *
 * Shared by the standings screen and the event hub, so the table you glance at on the hub is
 * the same object as the one you open to argue about.
 *
 * There is no qualification line. How many go through from a group is not something the
 * event records — the knockout is seeded from the finishing order when it is drawn — and a
 * dashed line under second place would be a rule the app made up.
 */
export function GroupTable({
  group,
  isTie,
  label,
  faces,
  isYours,
}: {
  group: StandingsGroup;
  isTie: boolean;
  label: (row: StandingRow) => string;
  faces: (row: StandingRow) => Person[] | null;
  /** Your row (or your team's), shaded the way the board shades yours. */
  isYours: (row: StandingRow) => boolean;
}) {
  const head =
    "font-mono pb-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-muted";

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b-2" style={{ borderColor: "var(--color-line)" }}>
          <th scope="col" className={`${head} w-8 text-left`}>
            #
          </th>
          <th scope="col" className={`${head} text-left`}>
            {isTie ? "Team" : "Pair"}
          </th>
          <th scope="col" className={`${head} w-8 text-right`}>
            <abbr title="Played" className="no-underline">
              P
            </abbr>
          </th>
          <th scope="col" className={`${head} w-8 text-right`}>
            <abbr title="Won" className="no-underline">
              W
            </abbr>
          </th>
          <th scope="col" className={`${head} w-12 text-right`}>
            <abbr title="Game difference" className="no-underline">
              ±G
            </abbr>
          </th>
        </tr>
      </thead>
      <tbody>
        {group.rows.map((row, index) => {
          const yours = isYours(row);
          const people = faces(row);
          const games = row.gamesFor - row.gamesAgainst;
          const cell = "font-mono py-2.5 text-right text-sm";

          return (
            <tr
              key={row.key}
              className="border-b"
              style={{
                borderColor: "var(--color-line-muted)",
                background: yours ? "var(--color-surface)" : undefined,
              }}
            >
              <td className="font-display py-2.5 pl-1 text-[22px]">{index + 1}</td>
              <td className="py-2.5">
                <span className="flex min-w-0 items-center gap-2">
                  {people ? <AvatarPair people={people} size={24} /> : null}
                  <span className={`truncate text-[15px] ${yours ? "font-bold" : "font-semibold"}`}>
                    {label(row)}
                  </span>
                </span>
                {/* Only where it did some work, and on its own line: "matches won" under
                    every row would be noise. Under the two that finished level, it is the
                    whole answer to why they are in this order. */}
                {row.separatedBy && row.separatedBy !== "won" ? (
                  <span className="font-mono mt-0.5 block text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                    above on {TIEBREAK_LABEL[row.separatedBy]}
                  </span>
                ) : null}
              </td>
              <td className={cell}>{row.played}</td>
              <td className={cell}>{row.won}</td>
              <td className={`${cell} pr-1`}>{games > 0 ? `+${games}` : games}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
